import { CommonModule } from "@angular/common";
import { Component, EventEmitter, inject, OnInit, Output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { OmniarkUiModule } from "@corp-ais/omniark-ui";
import { OmniarkDrawerService } from "@shared/services/drawer.service";
import {
  createEmptyPackageFilter,
  PackageFilterData,
} from "./enrichment-list-filter.model";
import { PackageFilterStateService } from "./enrichment-list-filter.state";

@Component({
  selector: "app-package-filter-drawer",
  imports: [CommonModule, FormsModule, OmniarkUiModule],
  templateUrl: "./enrichment-list-filter.html",
  styleUrls: ["./enrichment-list-filter.scss"],
})
export class PackageFilterDrawer implements OnInit {
  @Output() filterApplied = new EventEmitter<PackageFilterData>();
  @Output() filterCleared = new EventEmitter<void>();
  @Output() drawerClosed = new EventEmitter<void>();

  private readonly drawerService = inject(OmniarkDrawerService);
  private readonly filterState = inject(PackageFilterStateService);

  filterData: PackageFilterData = createEmptyPackageFilter();

  // Values MUST be the canonical TMF620 camelCase spellings — the BE
  // validates `lifecycleStatus` against that allow-list and answers 400 for
  // anything else (which blanked the whole list, not just the filter). Order
  // mirrors the lifecycle progression; `inDesign` leads because it is the
  // status freshly onboarded packages actually carry.
  lifecycleStatusOptions = [
    { value: "inDesign", label: "In design" },
    { value: "designed", label: "Designed" },
    { value: "active", label: "Active" },
    { value: "inActive", label: "Inactive" },
    { value: "launched", label: "Launched" },
    { value: "retired", label: "Retired" },
    { value: "obsolete", label: "Obsolete" },
    { value: "rejected", label: "Rejected" },
  ];

  productTypeOptions = [
    { value: "Prepaid", label: "Prepaid" },
    { value: "Postpaid", label: "Postpaid" },
  ];

  versionOptions = [
    { value: "1.0.4", label: "1.0.4 (PLM)" },
    { value: "2.0.0", label: "2.0.0 (CPC2.0)" },
  ];

  isSellableOptions = [
    { value: "true", label: "Sellable" },
    { value: "false", label: "Not sellable" },
  ];

  get searchActive(): boolean {
    return this.filterState.activeSearchTerm().trim().length > 0;
  }

  /** Checkbox view over `filterData.deleted` ("only" ⇄ checked). Keeps the
   *  stored value a string so the filter-count / request logic is uniform. */
  get deletedChecked(): boolean {
    return this.filterData.deleted === "only";
  }
  set deletedChecked(checked: boolean) {
    this.filterData.deleted = checked ? "only" : "";
  }

  /** Card-level toggle for the deleted filter — the label and hint are a much
   *  bigger target than the box, and users aim at them. */
  toggleDeleted(): void {
    this.deletedChecked = !this.deletedChecked;
  }

  ngOnInit(): void {
    // Seed from the applied filter so reopening the drawer shows what's in
    // effect (this component is re-created on every open).
    const applied = this.filterState.applied();
    if (applied) {
      this.filterData = { ...applied };
    }
  }

  onApply(): void {
    this.filterApplied.emit(this.filterData);
    this.onClose();
  }

  onClearAll(): void {
    this.filterData = createEmptyPackageFilter();
    this.filterCleared.emit();
  }

  onClose(): void {
    this.drawerClosed.emit();
    this.drawerService.closeAll();
  }
}
